import { Route, Routes } from 'react-router-dom'
import { AppShell } from './app/app-shell'
import { Landing } from './routes/landing'
import { Login } from './routes/login'
import { NotFound } from './routes/not-found'
import { PeopleList } from './routes/people'
import { CustomFieldsAdminPage } from './routes/directory/custom-fields-admin'
import { DirectoryListPage } from './routes/directory/directory-list'
import { PersonProfilePage } from './routes/directory/person-profile'
import { GroupsList } from './routes/groups/groups-list'
import { GroupDetail } from './routes/groups/group-detail'
import { TeamsList } from './routes/teams/teams-list'
import { TeamDetail } from './routes/teams/team-detail'
import { ListsList } from './routes/lists/index'
import { ListDetailPage } from './routes/lists/detail'
import { EventsList } from './routes/events/events-list'
import { EventDetail } from './routes/events/event-detail'
import { CalendarPage } from './routes/calendar'
import { CalendarFeedsPage } from './routes/calendar/feeds'
import { ResourcesList, ResourceNew, ResourceBookings } from './routes/resources'
import { FormsList, FormBuilder, FormResponses, PublicFormPage } from './routes/forms'
import { ContentHub } from './routes/content'
import { PagesAdmin } from './routes/content/pages'
import { PageEditor } from './routes/content/page-editor'
import { SermonsAdmin } from './routes/content/sermons'
import { SermonEditor } from './routes/content/sermon-editor'
import { ContentPublicView } from './routes/content/public-view'
import { WorkflowsList, WorkflowDetail, WorkflowRuns } from './routes/workflows'
import { CheckinsHub } from './routes/checkins/index'
import { CheckinKiosk } from './routes/checkins/kiosk'
import { CheckinRoster } from './routes/checkins/roster'
import { ServicePlansList, SongLibrary, PlanEditor } from './routes/service-planning'
import { AiPlayground } from './routes/ai/ai-playground'
import { Signup } from './routes/signup'
import { AcceptInvitePage, InvitationsPage, MembersList } from './routes/members'
import { NotificationsList } from './routes/notifications'
import { ReportsHub } from './routes/reports'
import { ReportsDashboard } from './routes/reports/dashboard'
import { SettingsPage } from './routes/settings'
import { SupportList, SupportNew, SupportDetail } from './routes/support'
import { SitesAdminPage } from './routes/settings/sites'
import { PlatformAdminPage } from './routes/platform'

export function App() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />

      {/* Public, unauthenticated routes — siblings of "/" (NOT under AppShell). */}
      <Route path="/f/:orgSlug/:formSlug" element={<PublicFormPage />} />
      <Route path="/invite/:token" element={<AcceptInvitePage />} />

      <Route path="/app" element={<AppShell />}>
        {/* Landing dashboard shows real org metrics from GET /reports/dashboard. */}
        <Route index element={<ReportsDashboard />} />
        <Route path="people" element={<PeopleList />} />

        {/* Directory */}
        <Route path="directory" element={<DirectoryListPage />} />
        <Route path="directory/custom-fields" element={<CustomFieldsAdminPage />} />
        <Route path="directory/:personId" element={<PersonProfilePage />} />

        {/* Groups & Teams */}
        <Route path="groups" element={<GroupsList />} />
        <Route path="groups/:id" element={<GroupDetail />} />
        <Route path="teams" element={<TeamsList />} />
        <Route path="teams/:id" element={<TeamDetail />} />

        {/* Lists */}
        <Route path="lists" element={<ListsList />} />
        <Route path="lists/:id" element={<ListDetailPage />} />

        {/* Events */}
        <Route path="events" element={<EventsList />} />
        <Route path="events/:id" element={<EventDetail />} />

        {/* Calendar */}
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="calendar/feeds" element={<CalendarFeedsPage />} />

        {/* Resources */}
        <Route path="resources" element={<ResourcesList />} />
        <Route path="resources/new" element={<ResourceNew />} />
        <Route path="resources/:id" element={<ResourceBookings />} />

        {/* Forms */}
        <Route path="forms" element={<FormsList />} />
        <Route path="forms/new" element={<FormBuilder />} />
        <Route path="forms/:id" element={<FormBuilder />} />
        <Route path="forms/:id/responses" element={<FormResponses />} />

        {/* Content */}
        <Route path="content" element={<ContentHub />} />
        <Route path="content/pages" element={<PagesAdmin />} />
        <Route path="content/pages/new" element={<PageEditor />} />
        <Route path="content/pages/:id" element={<PageEditor />} />
        <Route path="content/sermons" element={<SermonsAdmin />} />
        <Route path="content/sermons/new" element={<SermonEditor />} />
        <Route path="content/sermons/:id" element={<SermonEditor />} />
        <Route path="content/preview" element={<ContentPublicView />} />

        {/* Workflows */}
        <Route path="workflows" element={<WorkflowsList />} />
        <Route path="workflows/:id" element={<WorkflowDetail />} />
        <Route path="workflows/:id/runs" element={<WorkflowRuns />} />

        {/* Check-in */}
        <Route path="checkins" element={<CheckinsHub />} />
        <Route path="checkins/kiosk" element={<CheckinKiosk />} />
        <Route path="checkins/roster" element={<CheckinRoster />} />

        {/* Service Planning */}
        <Route path="service-planning" element={<ServicePlansList />} />
        <Route path="service-planning/songs" element={<SongLibrary />} />
        <Route path="service-planning/:id" element={<PlanEditor />} />

        {/* AI assist */}
        <Route path="ai" element={<AiPlayground />} />

        {/* Members & access */}
        <Route path="members" element={<MembersList />} />
        <Route path="members/invitations" element={<InvitationsPage />} />

        {/* Notifications */}
        <Route path="notifications" element={<NotificationsList />} />

        {/* Reports */}
        <Route path="reports" element={<ReportsHub />} />
        <Route path="reports/dashboard" element={<ReportsDashboard />} />

        {/* Support (tickets live in the external FC EMS; the ChMS backend
            proxies + signs the calls. The submit form is gated client-side on
            GET /support/access and server-side by support.submit). */}
        <Route path="support" element={<SupportList />} />
        <Route path="support/new" element={<SupportNew />} />
        <Route path="support/:id" element={<SupportDetail />} />

        {/* Settings */}
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/sites" element={<SitesAdminPage />} />

        {/* Platform admin (cross-org; gated client-side on the platform-status
            query and server-side by the platform-admin flag inside the API). */}
        <Route path="platform" element={<PlatformAdminPage />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}
